import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import BreadcrumbNav from '@/components/base/BreadcrumbNav';
import SEOSchema from '@/components/feature/SEOSchema';
import { getComicById, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const comic = id ? getComicById(id) : undefined;
  const category = comic ? categories.find((c) => c.slug === comic.category) : undefined;

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-4">
              漫画未找到
            </h1>
            <p className="text-foreground-500 mb-6">
              您要找的漫画可能已被移除或链接有误
            </p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOSchema
        pageType="Article"
        pageTitle={`${comic.title} - 韩国漫画在线 | 免费韩漫阅读`}
        pageDescription={`${comic.title}：${comic.description.substring(0, 120)} - 韩国漫画在线免费阅读。`}
        canonicalUrl={`https://koreancomics.cn/article/${comic.id}`}
        breadcrumbItems={[
          { label: '首页', to: '/' },
          { label: `${category?.name || ''}漫画`, to: `/category/${comic.category}` },
          { label: comic.title },
        ]}
        articleData={{
          headline: `${comic.title} - 韩国漫画在线免费阅读`,
          description: comic.description,
          datePublished: '2026-01-15',
          dateModified: comic.updateDate,
          authorName: comic.author,
          imageUrl: comic.coverImage,
        }}
      />
      <Navbar />
      <main className="pt-16">
        <article className="px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-4xl mx-auto">
            <BreadcrumbNav
              items={[
                { label: '首页', to: '/' },
                { label: `${category?.name || ''}漫画`, to: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-full sm:w-[240px] md:w-[280px]">
                <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国漫画在线阅读`}
                    title={`${comic.title} - 免费漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mt-2">
                  作者：<strong>{comic.author}</strong>
                </p>

                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      comic.status === 'completed'
                        ? 'bg-secondary-100 text-secondary-700'
                        : 'bg-primary-100 text-primary-700'
                    }`}
                  >
                    {comic.status === 'completed' ? '已完结' : '连载中'}
                  </span>
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-star-fill text-accent-400 w-3 h-3 flex items-center justify-center"></i>
                    {comic.rating} 分
                  </span>
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                    {(comic.views / 10000).toFixed(1)}万 阅读
                  </span>
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-file-list-3-line w-3 h-3 flex items-center justify-center"></i>
                    共 <strong>{comic.totalChapters}</strong> 话
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-background-100 text-foreground-600 rounded-md text-xs whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-5">
                  <h2 className="text-base font-heading text-foreground-900 mb-2">
                    作品简介
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="mt-5">
                  <p className="text-xs text-foreground-400">
                    最近更新：{comic.updateDate}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 md:mt-10">
              <h2 className="text-lg font-heading text-foreground-900 mb-4">
                章节列表
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="divide-y divide-background-200/50">
                  {comic.chapters.map((chapter) => (
                    <div
                      key={chapter.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-background-100/70 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-foreground-800">
                          第{chapter.number}话
                        </span>
                        <span className="text-sm text-foreground-600">
                          {chapter.title}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400 flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
                        {chapter.updateDate}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {comic.totalChapters > comic.chapters.length && (
                <div className="mt-4 text-center">
                  <button className="px-6 py-2.5 bg-background-100 text-foreground-600 rounded-md text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    加载更多章节 (+{comic.totalChapters - comic.chapters.length})
                  </button>
                </div>
              )}
            </div>

            <div className="mt-8 md:mt-10 flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                返回上一页
              </button>
              <Link
                to={`/category/${comic.category}`}
                className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                更多{category?.name || ''}漫画
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}